import React, { Fragment } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import RouteChangeLoading from "../component/RouteChangeLoading";
import SimpleBackdrop from "../component/loading/loading";
import { usePermissions } from "../contexts/permissionsContext";
import { RouteChangeProvider } from "../contexts/routeChangeContextProvider";
import useAuth from "../hooks/useAuth";
import DashboardScp from "../view/Dashboard-scp/dashboard-scp";
import NewAvailability from "../view/availability/insert-availability";
import ListBudget from "../view/budget/list-budget";
import NewCircuit from "../view/circuit/insert-circuit";
import ListCircuit from "../view/circuit/list-circuit";
import InsertConfiguration from "../view/configuration/insert-configuration";
import ListConfiguration from "../view/configuration/list-configuration";
import NewCustomer from "../view/customer/insert-customer";
import ListCustomer from "../view/customer/list-customer";
import DashboardSuporte from "../view/dash-suport/dashboad-suporte";
import ForgotPassword from "../view/forgot-password";
import Home from "../view/home/home";
import SignIn from "../view/login/login";
import ResetPassword from "../view/reset-password";
import NewPanel from "../view/panel/insert-panel";
import ListPanel from "../view/panel/list-panel";
import NewSale from "../view/sale/insert-sale";
import RecordInvoice from "../view/record-invoice/record-invoice";
import ListSale from "../view/sale/list-sale";
import NewService from "../view/service/insert-service";
import ListService from "../view/service/list-service";
import NewUser from "../view/user/insert-user";
import User from "../view/user/list-user";

const Loading = () => {
    return <SimpleBackdrop open={true} title="Carregando informações..." />;
}

// /**
//  * @function Private
//  * @description Rota privada que verifica se o usuário está logado
//  * @param {Object} Item - Componente a ser renderizado
//  */
// const Private = ({ Item }) => {
//     const { signed, loading } = useAuth();

//     if (loading) {
//         return Loading();
//     }

//     return signed > 0 ? <Item /> : <SignIn />;
// }

/**
 * @function ProtectedRoute
 * @description Rota protegida que verifica permissão e redireciona se necessário
 * @param {Object} Item - Componente a ser renderizado
 * @param {string} permission - Permissão a ser verificada
 * @param {string} action - Ação específica (islist, isinsert, etc.)
 * @param {string} redirectTo - Rota de redirecionamento
 */
const ProtectedRoute = ({ Item, permission, action = 'islist', redirectTo = '/dashboard', children }) => {
    const { signed, loading } = useAuth();
    const { hasPermissionAction, isLoading } = usePermissions();

    // Aguarda o carregamento da autenticação
    if (loading) {
        return Loading();
    }

    const tokenExists = !!localStorage.getItem("user_token");

    if (!signed || !tokenExists) {
        return <Navigate to="/login" replace />;
    }

    // Aguarda o carregamento das permissões
    if (isLoading) {
        return Loading();
    }

    // Se tem permissão específica, verifica se o usuário tem acesso
    if (permission && !hasPermissionAction(permission, action)) {
        return <Navigate to={redirectTo} replace />;
    }

    return children ? children : <Item />;
}

const RouteApp = () => {
    return (
        <BrowserRouter>
            <RouteChangeProvider>
                <RouteChangeLoading />
                <Fragment>
                    <Routes>
                    <Route path="/login" element={<SignIn />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    {/* Rotas públicas */}
                    <Route exact path="/" element={<ProtectedRoute Item={Home} />} />
                    <Route exact path="/dashboard" element={<ProtectedRoute Item={Home} />} />

                    {/* Rotas de Usuário */}
                    <Route exact path="/user" element={<ProtectedRoute Item={User} permission="Usuario" action="islist" />} />
                    <Route exact path="/newUser" element={<ProtectedRoute Item={NewUser} permission="Usuario" action="isinsert" />} />
                    <Route exact path="/newUser/:id" element={<ProtectedRoute Item={NewUser} permission="Usuario" action="islist" />} />
                    <Route exact path="/userList" element={<ProtectedRoute Item={User} permission="Usuario" action="islist" />} />

                    {/* Rotas de Painel */}
                    <Route exact path="/listPanel" element={<ProtectedRoute Item={ListPanel} permission="Painel" action="islist" />} />
                    <Route exact path="/panel/:id" element={<ProtectedRoute Item={NewPanel} permission="Painel" action="islist" />} />
                    <Route exact path="/panel" element={<ProtectedRoute Item={NewPanel} permission="Painel" action="isinsert" />} />

                    {/* Rotas de Cliente */}
                    <Route exact path="/listCustomer" element={<ProtectedRoute Item={ListCustomer} permission="Cliente" action="islist" />} />
                    <Route exact path="/customer/:id" element={<ProtectedRoute Item={NewCustomer} permission="Cliente" action="islist" />} />
                    <Route exact path="/customer" element={<ProtectedRoute Item={NewCustomer} permission="Cliente" action="isinsert" />} />

                    {/* Rotas de Serviço */}
                    <Route exact path="/listService" element={<ProtectedRoute Item={ListService} permission="Serviço" action="islist" />} />
                    <Route exact path="/service/:id" element={<ProtectedRoute Item={NewService} permission="Serviço" action="islist" />} />
                    <Route exact path="/service" element={<ProtectedRoute Item={NewService} permission="Serviço" action="isinsert" />} />

                    {/* Rotas de Circuito */}
                    <Route exact path="/listCircuit" element={<ProtectedRoute Item={ListCircuit} permission="Circuito" action="islist" />} />
                    <Route exact path="/circuit/:id" element={<ProtectedRoute Item={NewCircuit} permission="Circuito" action="islist" />} />
                    <Route exact path="/circuit" element={<ProtectedRoute Item={NewCircuit} permission="Circuito" action="isinsert" />} />

                    {/* Rotas de Vendas */}
                    <Route exact path="/listSale" element={<ProtectedRoute Item={ListSale} permission="Vendas" action="islist" />} />
                    <Route exact path="/sale/:id" element={<ProtectedRoute Item={NewSale} permission="Vendas" action="islist" />} />
                    <Route exact path="/saleReopen/:id/:isOpen" element={<ProtectedRoute Item={NewSale} permission="Reabrir Vendas" action="isinsert" />} />
                    <Route path="/saleParams" element={<ProtectedRoute Item={NewSale} permission="Vendas" action="isinsert" />} />
                    <Route exact path="/sale" element={<ProtectedRoute Item={NewSale} permission="Vendas" action="isinsert" />} />

                    {/* Rotas de Registro de Fatura */}
                    <Route
                      exact
                      path="/record-invoice"
                      element={
                        <ProtectedRoute
                          Item={RecordInvoice}
                          permission="Escrituracao Nota Fiscal"
                          action="isinsert"
                        />
                      }
                    />

                    {/* Rotas de Disponibilidade */}
                    <Route exact path="/availability" element={<ProtectedRoute Item={NewAvailability} />} />
                    <Route exact path="/availability/:id" element={<ProtectedRoute Item={NewAvailability} />} />

                    {/* Rotas de Dashboard */}
                    <Route exact path="/dashboardscp" element={<ProtectedRoute Item={DashboardScp} permission="Dashboard SCP" action="isinsert" />} />
                    <Route exact path="/dashboardSuport" element={<ProtectedRoute Item={DashboardSuporte} permission="Suporte" action="isinsert" />} />

                    {/* Rotas de Configuração */}
                    <Route exact path="/listConfiguration" element={<ProtectedRoute Item={ListConfiguration} permission="Configuração" action="islist" />} />
                    <Route exact path="/configuration" element={<ProtectedRoute Item={InsertConfiguration} permission="Configuração" action="isinsert" />} />
                    <Route exact path="/configuration/:id" element={<ProtectedRoute Item={InsertConfiguration} permission="Configuração" action="islist" />} />

                    {/* Rotas de Orçamento */}
                    <Route exact path="/budgetList" element={<ProtectedRoute Item={ListBudget} />} />

                    {/* Rota "curinga" - deve estar no final */}
                    <Route path="*" element={<SignIn />} />
                    </Routes>
                </Fragment>
            </RouteChangeProvider>
        </BrowserRouter>
    )
}

export default RouteApp;